import { motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { SITE } from "@/data/site";

const LINES = [
  "Professional Printing,",
  "Backed by 30+ Years",
  "of Experience.",
];

const containerV = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.15 } },
};
const lineV = {
  hidden: { y: "115%" },
  show: { y: "0%", transition: { duration: 1.05, ease: [0.7, 0, 0.2, 1] } },
};

export default function Hero() {
  const reduce = useReducedMotion();

  return (
    <section className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden" data-testid="hero-section">
      {/* Halftone backdrop */}
      <div className="absolute inset-0 halftone-radial pointer-events-none" aria-hidden />
      <div className="absolute inset-x-0 top-24 h-[560px] halftone-bg-lg opacity-40 halftone-fade pointer-events-none" aria-hidden />

      <div className="relative max-w-[1440px] mx-auto px-5 md:px-10">
        {/* eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.05 }}
          className="flex items-center gap-3 font-mono text-[11px] tracking-[0.32em] uppercase text-[color:var(--text-2)]"
        >
          <span className="w-8 h-px bg-[color:var(--text-2)]" /> Est. Print Craft · Since 1994
        </motion.div>

        {/* Kinetic headline */}
        <motion.h1
          variants={reduce ? undefined : containerV}
          initial={reduce ? { opacity: 0 } : "hidden"}
          animate={reduce ? { opacity: 1 } : "show"}
          transition={reduce ? { duration: 0.6 } : undefined}
          className="font-display mt-10 md:mt-14 text-[15vw] leading-[0.92] md:text-[112px] md:leading-[0.94] tracking-[-0.03em] text-[color:var(--text)]"
        >
          {LINES.map((l, i) => (
            <span key={i} className="block overflow-hidden pb-[0.05em]">
              <motion.span variants={reduce ? undefined : lineV} className="inline-block will-change-transform">
                {i === 2 ? (
                  <>
                    of <em className="italic text-[color:var(--accent)] font-medium">Experience.</em>
                  </>
                ) : (
                  l
                )}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-6 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="md:col-span-6 text-[17px] md:text-[19px] leading-[1.55] text-[color:var(--text-2)] max-w-[620px]"
          >
            {SITE.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="md:col-span-6 md:col-start-7 flex flex-wrap items-center gap-4 md:justify-end"
          >
            <Link
              to="/quote"
              className="btn-accent inline-flex items-center gap-3 rounded-full pl-6 pr-5 py-3.5 text-[12.5px] tracking-[0.16em] uppercase font-medium"
              data-testid="hero-quote-cta"
            >
              Request a Free Quote
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/services"
              className="btn-ghost-dark inline-flex items-center gap-3 rounded-full pl-6 pr-5 py-3.5 text-[12.5px] tracking-[0.16em] uppercase font-medium"
              data-testid="hero-services-cta"
            >
              Explore Our Services
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>

        {/* Feature photo strip */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.1, delay: 0.6, ease: [0.7, 0, 0.2, 1] }}
          className="relative mt-16 md:mt-24 rounded-[24px] overflow-hidden border border-black/5 bg-[color:var(--bg-dark)]"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 min-h-[340px] md:min-h-[520px]">
            <div className="md:col-span-8 relative overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1503694978374-8a2fa686963a?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
                alt="Offset printing press detail"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[color:var(--bg-dark)]/70 via-transparent to-transparent" />
              <div className="absolute left-6 md:left-10 bottom-6 md:bottom-10 text-white">
                <div className="font-mono text-[11px] tracking-[0.28em] uppercase opacity-70 mb-2">Featured · The Press Floor</div>
                <div className="font-display text-2xl md:text-4xl max-w-[520px] leading-tight">Where paper, ink, and precision meet.</div>
              </div>
              {/* dot overlay */}
              <div className="absolute inset-0 halftone-bg opacity-10 mix-blend-screen" />
            </div>
            <div className="md:col-span-4 relative bg-[color:var(--bg-dark)] text-white p-8 md:p-10 flex flex-col justify-between">
              <div>
                <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-white/60">Since 1994</div>
                <div className="mt-6 font-display text-6xl md:text-7xl leading-none">
                  30<span className="text-[color:var(--accent)]">+</span>
                </div>
                <div className="mt-3 text-white/70 text-sm">Years of printing experience across identity, publishing, and large format.</div>
              </div>
              <a
                href={SITE.phoneHref}
                className="mt-8 group inline-flex items-center gap-3 border-t border-white/15 pt-6 text-sm"
                data-testid="hero-phone"
              >
                <span className="font-mono tracking-[0.16em]">{SITE.phone}</span>
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </div>
          </div>
        </motion.div>

        {/* scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-10 flex items-center gap-2 font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--text-2)]"
        >
          <ArrowDown className="w-4 h-4 animate-bounce" /> Scroll to see our craft
        </motion.div>
      </div>
    </section>
  );
}
